import * as React from "react";
import { useNavigate } from "@opendash/router";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import styled from "styled-components";
import { AppLayout } from "./AppLayout";
import { useTexts } from "../hooks/useTexts";
import {
  TabContainer,
  TabButton,
  ContentContainer,
  ChartSection,
  Card,
  InfoBox,
  FigureCaption,
  SourcesSection,
} from "./common/StyledComponents";
import { LinkifyText } from "./common/LinkifyText";
import { processInlineFormatting } from "./common/RenderText";

const HighlightBox = styled.div`
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border-left: 4px solid #4caf50;
  padding: 20px;
  margin: 20px 0;
  border-radius: 0 8px 8px 0;

  &::before {
    content: "FAZIT";
    display: block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: #2e7d32;
    opacity: 0.6;
    margin-bottom: 8px;
  }

  p {
    margin-bottom: 8px;
  }
`;

export const PrivathaushaltVerbrauchRoute = React.memo(
  function PrivathaushaltVerbrauchRoute() {
    const navigate = useNavigate();
    const { t } = useTexts();

    const tabs = [
      {
        title: t("privathaushalt.tab.1.title", "Wasserverbrauch & Spartipps"),
        path: "/wasserklaerer/privathaushalt/verbrauch",
        isActive: true,
      },
      {
        title: t("privathaushalt.tab.2.title", "Der Wasserpreis"),
        path: "/wasserklaerer/privathaushalt/preis",
        isActive: false,
      },
      {
        title: t("privathaushalt.tab.3.title", "Härtegrad des Wassers"),
        path: "/wasserklaerer/privathaushalt/haerte",
        isActive: false,
      },
    ];

    // Chart-Daten für Wasserverbrauch im Haushalt
    const chartOptions = {
      chart: {
        type: "pie",
        height: 500,
      },
      title: {
        text: t(
          "privathaushalt.verbrauch.chart.title",
          "Aufteilung Wasserverbrauch im Haushalt"
        ),
        style: {
          fontSize: "20px",
          fontWeight: "bold",
          color: "#2c3e50",
        },
      },
      subtitle: {
        text: t(
          "privathaushalt.verbrauch.chart.subtitle",
          "Durchschnittlicher Verbrauch: 126 Liter pro Person und Tag"
        ),
        style: {
          fontSize: "16px",
          color: "#666",
        },
      },
      plotOptions: {
        pie: {
          allowPointSelect: false,
          cursor: "default",
          dataLabels: {
            enabled: true,
            format:
              "<b>{point.name}</b>: {point.y} L ({point.percentage:.1f}%)",
          },
          showInLegend: false,
          colors: [
            "#1565c0",
            "#4fc3f7",
            "#00897b",
            "#26a69a",
            "#66bb6a",
            "#1e88e5",
            "#a5d6a7",
          ],
        },
      },
      series: [
        {
          name: "Verbrauch",
          data: [
            {
              name: t(
                "privathaushalt.verbrauch.koerperpflege",
                "Körperpflege (Baden/Duschen)"
              ),
              y: 45,
            },
            {
              name: t("privathaushalt.verbrauch.wc", "Toilettenspülung"),
              y: 34,
            },
            {
              name: t("privathaushalt.verbrauch.waesche", "Wäsche waschen"),
              y: 15,
            },
            {
              name: t("privathaushalt.verbrauch.geschirr", "Geschirrspülen"),
              y: 7.5,
            },
            {
              name: t(
                "privathaushalt.verbrauch.raumreinigung",
                "Raumreinigung, Autopflege, Garten"
              ),
              y: 7.5,
            },
            {
              name: t("privathaushalt.verbrauch.kochen", "Essen und Trinken"),
              y: 5,
            },
            {
              name: t(
                "privathaushalt.verbrauch.kleingewerbe",
                "Kleingewerbeanteil und Sonstiges"
              ),
              y: 12,
            },
          ],
        },
      ],
      legend: {
        enabled: false,
      },
      tooltip: {
        pointFormat:
          "{series.name}: <b>{point.y} Liter ({point.percentage:.1f}%)</b>",
      },
      credits: { enabled: false },
    };

    return (
      <AppLayout>
        <ContentContainer>
          <h1>
            {t(
              "privathaushalt.verbrauch.title",
              "Wasserverbrauch im privaten Haushalt"
            )}
          </h1>

          <TabContainer>
            {tabs.map((tab, index) => (
              <TabButton
                key={index}
                isActive={tab.isActive}
                onClick={() => !tab.isActive && navigate(tab.path)}
              >
                {tab.title}
              </TabButton>
            ))}
          </TabContainer>

          <ChartSection>
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
            <FigureCaption>
              {t(
                "privathaushalt.verbrauch.chart.caption",
                "Abbildung 1: Aufteilung Wasserverbrauch im Haushalt [eigene Darstellung aus [1]]"
              )}
            </FigureCaption>
          </ChartSection>

          <Card>
            <h2>
              {t(
                "privathaushalt.verbrauch.section1.title",
                "Wie verteilt sich der Wasserverbrauch im Haushalt?"
              )}
            </h2>
            <p>
              {processInlineFormatting(
                t(
                  "privathaushalt.verbrauch.section1.text1",
                  "Im Schnitt nutzt jede Person täglich 126 Liter Trinkwasser im Haushalt. [1] Wie verteilt sich diese Menge auf die einzelnen Sektoren im Haushalt? Hier eine Übersicht:"
                )
              )}
            </p>
            <p>
              {t(
                "privathaushalt.verbrauch.section1.text2.main",
                "Den Großteil des Wassers verwenden wir für unsere Körperpflege, die Toilettenspülung und die Reinigung. Nur geringe Anteile fließen täglich für das Trinken und Kochen mit ein. Warum der Kleingewerbeanteil mit in den Wasserverbrauch von Privathaushalten fließt, wird im Kapitel"
              )}{" "}
              <a
                href="/wasserklaerer/wasserangebot/verbrauch"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/wasserklaerer/wasserangebot/verbrauch");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                style={{ color: "var(--primaryColor)" }}
              >
                {t(
                  "privathaushalt.verbrauch.section1.text2.link",
                  '"Wasserverbrauch in Deutschland"'
                )}
              </a>{" "}
              {t("privathaushalt.verbrauch.section1.text2.suffix", "geklärt.")}
            </p>
          </Card>

          <Card>
            <h2>
              {t(
                "privathaushalt.verbrauch.spartipps.title",
                "Wasserspartipps für den Haushalt"
              )}
            </h2>
            <p>
              {t(
                "privathaushalt.verbrauch.spartipps.intro",
                "Mit einfachen Maßnahmen lässt sich im Haushalt viel Wasser sparen. Hier finden Sie praktische Tipps für die verschiedenen Bereiche:"
              )}
            </p>

            {/* Körperpflege */}
            <h3>
              {t(
                "privathaushalt.verbrauch.tip.koerperpflege.title",
                "Körperpflege (Baden und Duschen) – 36%"
              )}
            </h3>
            <p>
              {t(
                "privathaushalt.verbrauch.tip.koerperpflege.intro",
                "Rund 45 Liter Wasser nutzen wir täglich für diesen Bereich."
              )}
            </p>
            <ul>
              <li>
                {processInlineFormatting(
                  t(
                    "privathaushalt.verbrauch.tip.koerperpflege.item1",
                    "Die Duschtemperatur spielt eine wesentliche Rolle: Bereits eine Absenkung um ein Grad Celsius kann erhebliche Mengen an CO₂-Emissionen sowie Kosten einsparen. Dies liegt daran, dass die Erwärmung von Wasser einen hohen Energieaufwand erfordert, der in den meisten Fällen mit CO₂-Emissionen verbunden ist. Warmwasser ist bis zu viermal so teuer wie Kaltwasser. [2]"
                  )
                )}
              </li>
              <li>
                {processInlineFormatting(
                  t(
                    "privathaushalt.verbrauch.tip.koerperpflege.item2",
                    "Ein Sparduschkopf verbraucht deutlich weniger Wasser als ein herkömmlicher. Dabei bleibt das angenehme Gefühl beim Duschen durch die spezielle Form der Brause aber gleich. Verbrauch, Kosten und CO₂-Emissionen lassen sich damit um die Hälfte senken. Der hohe Einspareffekt ergibt sich insbesondere daraus, dass beim Duschen überwiegend warmes Wasser genutzt wird und somit sowohl Wasser- als auch Energieeinsparungen erzielt werden. [2] [3]"
                  )
                )}
              </li>
              <li>
                {processInlineFormatting(
                  t(
                    "privathaushalt.verbrauch.tip.koerperpflege.item3",
                    'Durchflussbegrenzer: Der Einsatz eines Durchflussbegrenzers als Zwischenstück im Duschschlauch oder am Wasserhahn reduziert die durchströmende Wassermenge. In Kombination mit sogenannten "Perlatoren" (Luftsprudlern) wird dem Wasser Luft beigemischt, wodurch es fülliger wirkt. Als Verbraucher merken wir den Unterschied subjektiv kaum, obwohl der tatsächliche Wasserverbrauch sinkt. Die Anschaffungskosten liegen bei etwa 5 Euro. [3]'
                  )
                )}
              </li>
              <li>
                {processInlineFormatting(
                  t(
                    "privathaushalt.verbrauch.tip.koerperpflege.item4",
                    "Duschen statt Baden: Pro Badevorgang lassen sich im Durchschnitt rund 100 Liter Wasser einsparen. Tägliches Duschen ist aus hygienischen Gründen nicht notwendig und kann zudem die Haut strapazieren. [3]"
                  )
                )}
              </li>
            </ul>

            {/* Toilettenspülung */}
            <h3>
              {t(
                "privathaushalt.verbrauch.tip.wc.title",
                "Toilettenspülung – 27%"
              )}
            </h3>
            <p>
              {t(
                "privathaushalt.verbrauch.tip.wc.intro",
                "Rund 34 Liter Wasser nutzen wir täglich für diesen Bereich."
              )}
            </p>
            <ul>
              <li>
                {processInlineFormatting(
                  t(
                    "privathaushalt.verbrauch.tip.wc.item1",
                    "Sparspülung: Täglich jagen wir eimerweise Trinkwasser durch die Klospülung. Der Austausch eines alten Spülkastens durch einen moderneren kann bei gleicher Spülleistung den Wasserverbrauch deutlich senken. Alternativ können ältere Spülsysteme durch den Einbau von Spülstopp- bzw. Wasserstopptasten nachgerüstet werden. Die Kosten hierfür liegen je nach Ausführung bei etwa 5 bis 15 Euro. [3]"
                  )
                )}
              </li>
            </ul>

            {/* Wäschewaschen */}
            <h3>
              {t(
                "privathaushalt.verbrauch.tip.waesche.title",
                "Wäsche waschen – 12%"
              )}
            </h3>
            <p>
              {t(
                "privathaushalt.verbrauch.tip.waesche.intro",
                "Rund 15 Liter Wasser nutzen wir täglich für diesen Bereich."
              )}
            </p>
            <ul>
              <li>
                {processInlineFormatting(
                  t(
                    "privathaushalt.verbrauch.tip.waesche.item1",
                    "Setzen Sie nur volle Maschinen in Gang - auf eine Vorwäsche kann in den meisten Fällen verzichtet werden. Beim Neukauf ist zudem auf eine hohe Effizienz zu achten, wobei der Wasserverbrauch idealerweise 10 bis 12 Liter pro Kilogramm Wäsche nicht überschreiten sollte. [3]"
                  )
                )}
              </li>
              <li>
                {processInlineFormatting(
                  t(
                    "privathaushalt.verbrauch.tip.waesche.item2",
                    "Modelle, die 20 Jahre und älter sind, verbrauchen bis zu 200 Liter pro Waschgang. Da kann es sich lohnen auf neuere Modelle umzusteigen, die ihren Wasserverbrauch durch zusätzliche Sparfunktionen auf bis unter 35 Liter pro Waschgang reduzieren können. Das funktioniert zum Beispiel durch Sensoren, die die Verschmutzung des Wassers messen. So kann leicht verschmutztes Wasser innerhalb eines Waschgangs öfter genutzt werden. Eine konkrete Angabe, wie viel Wasser Ihr Modell verbraucht, liefert das Energieeffizienzlabel auf der Maschine. [2]"
                  )
                )}
              </li>
              <li>
                {t(
                  "privathaushalt.verbrauch.tip.waesche.item3",
                  "Nutzen Sie bevorzugterweise das Eco-Programm. In den meisten Fällen reicht dieses aus, denn es nutzt als Ausgleich zu den geringeren Temperaturen längere Waschzeiten. Moderne Waschmittel sind auch bei niedrigeren Temperaturen wirksam."
                )}
              </li>
            </ul>

            {/* Geschirrspülen */}
            <h3>
              {t(
                "privathaushalt.verbrauch.tip.geschirr.title",
                "Geschirrspülen – 6%"
              )}
            </h3>
            <p>
              {t(
                "privathaushalt.verbrauch.tip.geschirr.intro",
                "Rund 7,5 Liter Wasser nutzen wir täglich für diesen Bereich."
              )}
            </p>
            <ul>
              <li>
                {processInlineFormatting(
                  t(
                    "privathaushalt.verbrauch.tip.geschirr.item1",
                    "Zunächst ist festzuhalten: Geschirrspülmaschinen sind im Vergleich zum Handspülen aus ökologischer Sicht deutlich effizienter. Der maschinelle Spülvorgang benötigt im Durchschnitt rund 50 % weniger Wasser und etwa 28 % weniger Energie als das Spülen von Hand. [4]"
                  )
                )}
              </li>
              <li>
                {processInlineFormatting(
                  t(
                    "privathaushalt.verbrauch.tip.geschirr.item2",
                    "Geräte mit einem Alter von über zehn Jahren sind meist sehr ineffizient. Da ist der Austausch gegen ein modernes Gerät der Effizienzklasse A in der Regel sinnvoll, da sich dadurch Wasserverbrauch, Energiebedarf und Betriebskosten deutlich reduzieren lassen. [2]"
                  )
                )}
              </li>
              <li>
                {processInlineFormatting(
                  t(
                    "privathaushalt.verbrauch.tip.geschirr.item3",
                    "Ein Vorspülen des Geschirrs unter fließendem Wasser ist insbesondere bei modernen Spülmaschinen nicht erforderlich und führt vielmehr zu einem unnötigen Mehrverbrauch. [4]"
                  )
                )}
              </li>
              <li>
                {processInlineFormatting(
                  t(
                    "privathaushalt.verbrauch.tip.geschirr.item4",
                    "Beladen Sie Ihre Spülmaschine voll. Spülgänge mit nur teilgefüllten Körben sind aus ökologischer Sicht ineffizient. Eine Vergleichsstudie der Universität Bonn, an der rund 200 Haushalte in Deutschland, Italien, Schweden und Großbritannien teilnahmen, zeigte, dass viele Spülmaschinen lediglich zur Hälfte beladen werden. Es ließe sich etwa jeder zehnte Spülgang einsparen, wenn die Geräte konsequent voll beladen würden. Das passiert unter anderem, weil Geschirrteile zwischendurch unter fließendem Wasser gereinigt, anstatt gesammelt zu werden. [4]"
                  )
                )}
              </li>
              <li>
                {processInlineFormatting(
                  t(
                    "privathaushalt.verbrauch.tip.geschirr.item5",
                    "Viele Haushalte nutzen unnötig hohe Spültemperaturen. Für die meisten Verschmutzungsgrade des Geschirrs ist der Eco-Modus mit niedrigeren Temperaturen ausreichend und ermöglicht zusätzliche Einsparungen bei Energieverbrauch und CO₂-Emissionen. [4]"
                  )
                )}
              </li>
              <li>
                {t(
                  "privathaushalt.verbrauch.tip.geschirr.item6",
                  "Hat ein Haushalt keine Spülmaschine oder wenn doch mal per Hand gespült wird, sollte darauf geachtet werden, dass Wasser nicht unnötig kontinuierlich fließt. Effizienter ist die Nutzung von Wasserbädern in der Spüle oder das vorherige Einseifen des Geschirrs, wobei Wasser lediglich in der Phase des Abspülens verwendet wird."
                )}
              </li>
            </ul>

            {/* Raumreinigung, Garten */}
            <h3>
              {t(
                "privathaushalt.verbrauch.tip.raumreinigung.title",
                "Raumreinigung, Autopflege, Garten – 6%"
              )}
            </h3>
            <p>
              {t(
                "privathaushalt.verbrauch.tip.raumreinigung.intro",
                "Rund 7,5 Liter Wasser nutzen wir täglich für diesen Bereich."
              )}
            </p>
            <ul>
              <li>
                {processInlineFormatting(
                  t(
                    "privathaushalt.verbrauch.tip.raumreinigung.item1",
                    "Das Bewässern von Rasenflächen ist lediglich bei anhaltender extremer Trockenheit erforderlich. Erfolgen sollte es vorzugsweise in den Morgen- oder Abendstunden, um Verdunstungsverluste zu minimieren. Da für die Gartenbewässerung keine Trinkwasserqualität benötigt wird, erweist sich die Nutzung von Dachablaufwasser und Regentonnen als umweltfreundlich. Denn der Wasserkreislauf schließt sich dadurch kleinräumiger, ohne den Aufbereitungsaufwand der Wasserwerke zu beanspruchen. [3] [5]"
                  )
                )}
              </li>
              <li>
                {processInlineFormatting(
                  t(
                    "privathaushalt.verbrauch.tip.raumreinigung.item2",
                    "Gießen Sie lieber seltener und dafür intensiver als täglich wenig. Sie können zudem effizienter gießen, indem nicht auf Blätter sondern nahe am Boden gegossen wird. [6]"
                  )
                )}
              </li>
              <li>
                {processInlineFormatting(
                  t(
                    "privathaushalt.verbrauch.tip.raumreinigung.item3",
                    "Lassen Sie Ihr Fahrzeug bestenfalls in einer Waschanlage reinigen, da diese heutzutage in der Regel mit Kreislaufwassersystemen ausgestattet sind und somit den Wasserverbrauch deutlich reduzieren. Darüber hinaus wird sichergestellt, dass Reinigungsmittel, Straßenschmutz sowie mögliche Öl- und Schadstoffreste fachgerecht entsorgt werden, anstatt in den Boden oder das Grundwasser zu gelangen. [5]"
                  )
                )}
              </li>
            </ul>

            {/* Essen und Trinken */}
            <h3>
              {t(
                "privathaushalt.verbrauch.tip.kochen.title",
                "Essen und Trinken – 4%"
              )}
            </h3>
            <p>
              {t(
                "privathaushalt.verbrauch.tip.kochen.intro",
                "Rund 5 Liter Wasser nutzen wir täglich für Trinken und Lebensmittelzubereitung. Die Einsparpotenziale in diesem Bereich sind begrenzt."
              )}
            </p>
            <ul>
              <li>
                {t(
                  "privathaushalt.verbrauch.tip.kochen.item1",
                  "Es ist nicht erforderlich einen Nudeltopf bis zum Rand mit Wasser zu füllen; in den meisten Fällen genügt bereits etwa ein Drittel des Topfvolumens. Dadurch muss weniger Wasser erhitzt werden, was zugleich den Energiebedarf und die damit verbundenen CO₂-Emissionen senkt."
                )}
              </li>
              <li>
                {processInlineFormatting(
                  t(
                    "privathaushalt.verbrauch.tip.kochen.item2",
                    "Da eine ausreichende Flüssigkeitszufuhr aus gesundheitlicher Sicht essenziell ist, sollte hier bewusst nicht gespart werden. Der Konsum von Leitungswasser ist in Deutschland gesundheitlich unbedenklich. Im Gegensatz zu abgefülltem Wasser ist Leitungswasser erheblich günstiger und beansprucht keine Aufwendungen für Verpackung und Transport. Wenn Sie Leitungswasser trinken, tun Sie also nicht nur der Umwelt, sondern auch Ihrem Geldbeutel etwas Gutes. [5]"
                  )
                )}
              </li>
            </ul>
          </Card>

          <Card>
            <InfoBox>
              <h4>
                {t(
                  "privathaushalt.verbrauch.hygiene.title",
                  "Wasserhygienetipp"
                )}
              </h4>
              <p>
                {processInlineFormatting(
                  t(
                    "privathaushalt.verbrauch.hygiene.text",
                    "Trinkwasser gilt in Deutschland als das am strengsten überwachte Lebensmittel. Hat das Wasser jedoch über einen längeren Zeitraum in den Leitungen gestanden – etwa in den frühen Morgenstunden oder nach längerer Abwesenheit –, sollte es aus hygienischen Gründen vor der Lebensmittelzubereitung und dem Zähneputzen so lange ablaufen, bis es frisch und kalt aus der Leitung fließt. Das dabei ablaufende Wasser kann sinnvoll weiterverwendet werden, beispielsweise zur Bewässerung von Zimmerpflanzen. [5]"
                  )
                )}
              </p>
            </InfoBox>
          </Card>

          <HighlightBox>
            <p>
              <strong>
                {t("privathaushalt.verbrauch.fazit.title", "Zusammenfassung")}
              </strong>
            </p>
            <p>
              {t(
                "privathaushalt.verbrauch.fazit.text",
                "Durch bewussten Umgang mit Wasser lässt sich im Haushalt viel einsparen – sowohl bei den Wasserkosten als auch beim Energieverbrauch. Die größten Einsparpotenziale liegen in der Körperpflege, bei der Toilettenspülung und beim Wäschewaschen."
              )}
            </p>
          </HighlightBox>

          <SourcesSection>
            <h3>
              {t(
                "privathaushalt.verbrauch.literature.title",
                "Literaturverzeichnis"
              )}
            </h3>
            <ol>
              <li id="quelle-1">
                <LinkifyText>
                  {t(
                    "privathaushalt.verbrauch.literature.ref1",
                    '[1] Umweltbundesamt (Hrsg.), "Wassernutzung privater Haushalte", Umweltbundesamt, 2024. Zugegriffen: 10. Dezember 2025. [Online]. Verfügbar unter: https://www.umweltbundesamt.de/daten/private-haushalte-konsum/wohnen/wassernutzung-privater-haushalte'
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
              <li id="quelle-2">
                <LinkifyText>
                  {t(
                    "privathaushalt.verbrauch.literature.ref2",
                    '[2] A. Weißbach, "Durchschnittlicher Wasserverbrauch & Kosten", co2online, 2024. Zugegriffen: 10. Dezember 2025. [Online]. Verfügbar unter: https://www.co2online.de/energie-sparen/wasser-sparen/wasserverbrauch/durchschnittlicher-wasserverbrauch/'
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
              <li id="quelle-3">
                <LinkifyText>
                  {t(
                    "privathaushalt.verbrauch.literature.ref3",
                    '[3] Verbraucherzentrale (Hrsg.), "Trinkwasser sparen und sauber halten: So geht\'s", Verbraucherzentrale, 2025. Zugegriffen: 10. Dezember 2025. [Online]. Verfügbar unter: https://www.verbraucherzentrale.de/wissen/umwelt-haushalt/wasser/trinkwasser-sparen-und-sauber-halten-so-gehts-13926'
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
              <li id="quelle-4">
                <LinkifyText>
                  {t(
                    "privathaushalt.verbrauch.literature.ref4",
                    '[4] Universität Bonn (Hrsg.), "Verbraucher nutzen ihre Spülmaschinen zu ineffizient", Universität Bonn, 2011. Zugegriffen: 12. Dezember 2025. [Online]. Verfügbar unter: https://www.uni-bonn.de/de/universitaet/presse-kommunikation/presseservice/archiv-pressemitteilungen/2011/058-2011'
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
              <li id="quelle-5">
                <LinkifyText>
                  {t(
                    "privathaushalt.verbrauch.literature.ref5",
                    '[5] C. Baumgarten, J. Rechenberg, S. Richter, I. Chorus, L. Vigelahn, und O. Schmoll, "Wassersparen in Privathaushalten: sinnvoll, ausgereizt, übertrieben? - Fakten, Hintergründe, Empfehlungen", Umweltbundesamt, 2014. [Online]. Verfügbar unter: https://www.umweltbundesamt.de/sites/default/files/medien/479/publikationen/hgp_wassersparen_in_privathaushalten_web.pdf'
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
              <li id="quelle-6">
                <LinkifyText>
                  {t(
                    "privathaushalt.verbrauch.literature.ref6",
                    '[6] Umweltbundesamt (Hrsg.), "Tipps für eine nachhaltige Regenwassernutzung", Umweltbundesamt, 2025. Zugegriffen: 15. Dezember 2025. [Online]. Verfügbar unter: https://www.umweltbundesamt.de/umwelttipps-fuer-den-alltag/garten-freizeit/regenwassernutzung'
                  ).replace(/^\[\d+\]\s*/, "")}
                </LinkifyText>
              </li>
            </ol>
          </SourcesSection>
        </ContentContainer>
      </AppLayout>
    );
  }
);
